import { useMemo } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { EmissionTier, CanonicalPeriod } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { WildfireScoreBadge } from '../../common/fireRisk/utils'
import { useCurrentUser } from '../../../../hooks/users'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { renderHelpLines } from '../../common/aggregateHelpText'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatDamageTargetLabel,
  formatPlainNumber,
  REVENUE_AAL_LABEL,
  sumMetricNode,
  TOTAL_AAL_LABEL,
} from '../../common/financialMetrics'
import {
  getMetricKeys,
  getPeriodNode,
  getRecordKeys,
  getRecordValue,
  getSelectedPeriodPointY,
  getSucceededMetricData,
  getTotalAalValue,
  hasAnyCanonicalPeriodData,
  sumSelectedPeriodNode,
} from '../overviewMetrics'

const SCORE_METRIC_KEY = 'wf:score'
const DAMAGE_AAL_METRIC_KEY = 'wf:damage_loss'
const REVENUE_AAL_METRIC_KEY = 'wf:bi_loss'
const BI_DAYS_METRIC_KEY = 'wf:bi_days'
const FIRES_METRIC_KEY = 'wf:fires'

export default function WildfireSummary({
  propertyId,
  emissionTier,
  selectedPeriod,
}: {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId })
  const {
    data: damageAalData,
    isLoading: damageAalLoading,
    error: damageAalError,
  } = usePropertyClimateData(DAMAGE_AAL_METRIC_KEY, { propertyId })
  const {
    data: revenueAalData,
    isLoading: revenueAalLoading,
    error: revenueAalError,
  } = usePropertyClimateData(REVENUE_AAL_METRIC_KEY, { propertyId })
  const {
    data: biDaysData,
    isLoading: biDaysLoading,
    error: biDaysError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId })
  const { data: firesData } = usePropertyClimateData(FIRES_METRIC_KEY, { propertyId })

  const { currencySymbol } = useCurrentUser()

  const scorePayload = getSucceededMetricData(scoreData)
  const damageAalPayload = getSucceededMetricData(damageAalData)
  const revenueAalPayload = getSucceededMetricData(revenueAalData)
  const biDaysPayload = getSucceededMetricData(biDaysData)
  const firesPayload = getSucceededMetricData(firesData)

  const scoreValue = getSelectedPeriodPointY(scorePayload, emissionTier, selectedPeriod)
  const damageAalPeriodNode = getPeriodNode(damageAalPayload, emissionTier, selectedPeriod)
  const revenueAalPeriodNode = getPeriodNode(revenueAalPayload, emissionTier, selectedPeriod)
  const biDaysPeriodNode = getPeriodNode(biDaysPayload, emissionTier, selectedPeriod)
  const firesNode = getPeriodNode(firesPayload, emissionTier, selectedPeriod)

  const damageAalTotal = sumMetricNode(damageAalPeriodNode)
  const revenueAalTotal = sumMetricNode(revenueAalPeriodNode)
  const biDaysTotal = sumMetricNode(biDaysPeriodNode)
  const firesValue = sumMetricNode(firesNode)
  const totalAalValue = getTotalAalValue(damageAalTotal, revenueAalTotal)

  const damageTargets = useMemo(() => {
    const explicit = getMetricKeys(damageAalPayload, 'target')
    if (explicit.length) return explicit
    return getRecordKeys(damageAalPeriodNode)
  }, [damageAalPayload, damageAalPeriodNode])

  const damageBreakdownHasData = damageTargets.some(
    target => sumMetricNode(getRecordValue(damageAalPeriodNode, target)) != null,
  )

  const loading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const allErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)
  const hasAnyOverviewData = useMemo(
    () =>
      hasAnyCanonicalPeriodData([
        period => getSelectedPeriodPointY(scorePayload, emissionTier, period),
        period => sumSelectedPeriodNode(damageAalPayload, emissionTier, period),
        period => sumSelectedPeriodNode(revenueAalPayload, emissionTier, period),
        period => sumSelectedPeriodNode(biDaysPayload, emissionTier, period),
      ]),
    [biDaysPayload, damageAalPayload, emissionTier, revenueAalPayload, scorePayload],
  )
  const riskRatingHelpText = 'Wildfire risk rating for the selected horizon and emissions scenario.'
  const totalAalHelpText = renderHelpLines([
    `${TOTAL_AAL_LABEL} = Structure Damage AAL + Contents Damage AAL + BI AAL.`,
    'Shown for the selected horizon and emissions scenario.',
  ])
  const damageAalHelpText = 'Annualised structure and contents damage loss for the selected horizon.'
  const revenueAalHelpText = 'Annualised business interruption revenue loss for the selected horizon.'
  const biDaysHelpText = 'Expected business interruption days per year for the selected horizon.'

  if (loading && !scoreData && !damageAalData && !revenueAalData && !biDaysData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Could not load wildfire summary data.</Alert>
  }

  if (!hasAnyOverviewData && !loading) {
    return <Alert severity="info">No wildfire summary data is available for this property.</Alert>
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <ClimateSectionTitle
          title="Overview"
          subtitle="Summary of wildfire risk for the selected period"
          helpText="Overview metrics use the horizon selected in the page header."
          loading={loading}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="Risk Rating" helpText={riskRatingHelpText} loading={scoreLoading}>
            {scoreValue != null ? (
              <WildfireScoreBadge score={scoreValue} size="compact" />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Wildfire risk rating is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard
            title={TOTAL_AAL_LABEL}
            helpText={totalAalHelpText}
            loading={damageAalLoading || revenueAalLoading}
          >
            {totalAalValue != null ? (
              <Typography variant="h4">{formatCurrencyValue(totalAalValue, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Total AAL data is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard title={BI_DAYS_LABEL} helpText={biDaysHelpText} loading={biDaysLoading}>
            {biDaysTotal != null ? (
              <Typography variant="h4">{`${formatPlainNumber(biDaysTotal, 1)} days/year`}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Business interruption days are unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>

      <Box mt={4}>
        <ClimateSectionTitle
          title={FINANCIAL_BREAKDOWN_TITLE}
          subtitle="Damage and revenue components of annualised wildfire impacts"
          helpText="Financial metrics are annualised expected losses for the selected horizon and emissions scenario."
        />
      </Box>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={6}>
          <ClimateMetricCard title={DAMAGE_AAL_LABEL} helpText={damageAalHelpText} loading={damageAalLoading}>
            {damageAalTotal != null ? (
              <Typography variant="h4">{formatCurrencyValue(damageAalTotal, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Damage AAL is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ClimateMetricCard title={REVENUE_AAL_LABEL} helpText={revenueAalHelpText} loading={revenueAalLoading}>
            {revenueAalTotal != null ? (
              <Typography variant="h4">{formatCurrencyValue(revenueAalTotal, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                BI AAL is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <ClimateSectionTitle
                title="Damage AAL Breakdown"
                subtitle="Structure and contents split for annualised wildfire damage"
                helpText="Breaks Damage AAL into structure and contents components where available."
              />
              <Box mt={2}>
                {!damageBreakdownHasData ? (
                  <Typography variant="body2" color="text.secondary">
                    No damage breakdown is available for this period.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {damageTargets.map(target => (
                        <TableRow key={target}>
                          <TableCell>{formatDamageTargetLabel(target)}</TableCell>
                          <TableCell align="right">
                            {formatCurrencyValue(
                              sumMetricNode(getRecordValue(damageAalPeriodNode, target)),
                              currencySymbol,
                              0,
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <ClimateSectionTitle
                title="Wildfire Activity"
                subtitle="Lower-detail wildfire signal for the selected period"
                helpText="Estimated annual fire count for the selected horizon and emissions scenario."
              />
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Estimated fires per year
                </Typography>
                <Typography variant="h4">{firesValue != null ? formatPlainNumber(firesValue, 1) : '—'}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
